import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { theme } from 'lib/style';
import { MdAdd, MdSave } from 'react-icons/md';
import { SuperAdminCustomerDetails } from 'lib/context';
import { CardType, StatsCard } from './StatsCard';
import { VerificationStatus } from 'lib/const/VerificationStatus';
import { ButtonSwitch } from '../../components/ButtonSwitch';
import { IoIosLogIn } from 'react-icons/io';
import { ActionButtonWithTooltip } from '../../components/ActionButtonWithTooltip';
import { MdFileDownload } from 'react-icons/md';
import { ActionButton } from '../../components/ActionButton';

export enum TABS {
  USERS,
  CUSTOMER_DETAILS,
  OPT_OUT_REPORTS,
  ACTIVITY,
}

const HeaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 32px;
`;

const Heading = styled.div`
  font-family: 'Work Sans';
  margin: 0;
  text-align: left;
  font-weight: 800;
  font-size: 24px;
  line-height: 40px;
  line-break: anywhere;
  overflow: hidden;
  height: 40px;
  max-width: 135px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const LeftHeaderWrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const MiddleHeaderWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const RightHeaderWrapper = styled.div`
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
`;

const views = [
  {
    value: 'enabled',
    text: 'Active',
  },
  {
    value: 'disabled',
    text: 'Inactive',
  },
];

type Props = {
  isNewCustomer: boolean;
  customer?: SuperAdminCustomerDetails;
  tab: TABS;
  showStatusSwitch: boolean;
  importUsers(): void;
  onAddNewUser(): void;
  onStatusChange(value: VerificationStatus): void;
  exportUsers(): void;
  manageDefaults(): void;
};

export const CustomersDetailsHeader = ({
  isNewCustomer,
  customer,
  tab,
  showStatusSwitch,
  onAddNewUser,
  onStatusChange,
  importUsers,
  exportUsers,
  manageDefaults,
}: Props) => {
  const themes = useTheme();
  const disableAddUsers =
    !isNewCustomer &&
    tab !== TABS.CUSTOMER_DETAILS &&
    !!customer &&
    (customer.userCount ?? 0) >= (customer.maxUsers ?? 0);

  const buttonText = isNewCustomer
    ? 'Create Customer'
    : tab === TABS.CUSTOMER_DETAILS
      ? 'Save Changes'
      : 'New User';
  const icon =
    isNewCustomer || tab === TABS.CUSTOMER_DETAILS ? (
      <MdSave color={theme.palette.white || '#fff'} />
    ) : (
      <MdAdd color={theme.palette.white || '#fff'} />
    );
  return (
    <HeaderWrapper>
      <LeftHeaderWrapper>
        <Heading color={theme.palette.gray100}>
          {isNewCustomer ? 'New Customer' : customer?.business}
        </Heading>
        {!isNewCustomer && showStatusSwitch && (
          <ButtonSwitch
            defaultValue={customer?.verified ? 'enabled' : 'disabled'}
            values={views}
            onChange={newView => {
              onStatusChange(
                newView === 'enabled'
                  ? VerificationStatus.ACTIVE
                  : VerificationStatus.INACTIVE
              );
            }}
          />
        )}
      </LeftHeaderWrapper>
      {!isNewCustomer && (
        <MiddleHeaderWrapper>
          <StatsCard count={customer?.userCount ?? 0} text={'active users'} />
          <StatsCard
            count={customer?.maxUsers ?? 0}
            text={'user seats'}
            cardType={CardType.SECONDARY}
          />
        </MiddleHeaderWrapper>
      )}
      <RightHeaderWrapper>
        {!isNewCustomer && tab === TABS.USERS && (
          <ActionButton
            textPadding='8px 16px 8px 16px'
            backgroundColor={theme.palette.primaryDarkBlue10Percent}
            textColor={theme.palette.black_1_100}
            onClick={manageDefaults}
            text='Manage defaults'
          />
        )}
        {!isNewCustomer && tab === TABS.USERS && (
          <ActionButton
            icon={<MdFileDownload color={theme.palette.black_1_100} />}
            textPadding='8px 16px 8px 0'
            backgroundColor={theme.palette.primaryDarkBlue10Percent}
            textColor={theme.palette.black_1_100}
            onClick={exportUsers}
            text='Export Users'
          />
        )}
        {!isNewCustomer && tab === TABS.USERS && (
          <ActionButtonWithTooltip
            backgroundColor={themes.colors.secondaryButtonBgColor}
            icon={<IoIosLogIn />}
            textPadding='8px 16px 8px 0'
            onClick={importUsers}
            text='Import'
            showTooltipOnClick={disableAddUsers}
            tooltipTitle='Max users limit reached'
            tooltipMessage='For adding more users, increase your user seats'
          />
        )}
        {(tab === TABS.USERS || tab === TABS.CUSTOMER_DETAILS) && (
          <ActionButtonWithTooltip
            backgroundColor={theme.palette.covideoOrange100}
            textColor={theme.palette.white}
            icon={icon}
            textPadding='8px 16px 8px 0'
            onClick={onAddNewUser}
            text={buttonText}
            showTooltipOnClick={disableAddUsers}
            tooltipTitle='Max users limit reached'
            tooltipMessage='For adding more users, increase your user seats'
          />
        )}
      </RightHeaderWrapper>
    </HeaderWrapper>
  );
};
